/**
 * Админ · окно 5 запуска — экран владельца ВНУТРИ того же Mini App.
 *
 * ⛔ Отдельной веб-панели нет и не будет (решение плана запуска, разд. 4): это второй
 * адрес, вторая авторизация и второй фронт ради тех же цифр.
 *
 * ⭐⭐ Экран НИЧЕГО не решает о доступе. Право админа лежит в `.env` (`LANG_ADMIN_IDS`)
 * и проверяется ядром: постороннему `GET /admin` отвечает 403, и даже если он как-то
 * откроет этот экран, данных он не увидит. Кнопка в настройках — удобство, не защита.
 *
 * ⭐ И ни одной своей цифры: люди, занятия, оценки и заявки приезжают собранными
 * (`core/admin.py` + `core/feedback.py` + `core/leaderboard.py`).
 *
 * В навигацию экран НЕ добавлен: нижних кнопок по-прежнему пять.
 */

import { adminGift, adminTicket, adminView } from "../api";
import { STATUS_ORDER, averageLabel, distributionRows, noisyNote, ratingsWord,
         statusLabel, ticketsWord } from "../feedback";
import { esc } from "./feedback";
import { render } from "../router";
import { state } from "../state";
import { tap } from "../tg";

let loading = false;
let failed = false;
let openTicket: number | null = null;
let busy = false;

/** Сходить за свежей сводкой. ⭐ Заодно ядро догоняет неотправленные уведомления. */
async function load(): Promise<void> {
  if (loading) return;
  loading = true;
  failed = false;
  try {
    state.admin = await adminView();
  } catch {
    failed = true;                        // 403 у постороннего — и это правильно
  } finally {
    loading = false;
    render("admin");
  }
}

export function refreshAdmin(): void {
  void load();
}

const num = (n: number) => n.toLocaleString("ru-RU");
const kb = (bytes: number) => `${Math.round(bytes / 1024).toLocaleString("ru-RU")} КБ`;

function ratingsHtml(): string {
  const r = state.admin?.ratings;
  if (!r) return "";
  const rows = distributionRows(r).map((row) =>
    '<div class="distrow">' +
      `<span class="ds">${"★".repeat(row.stars)}</span>` +
      `<span class="dbar"><i style="width:${row.share}%"></i></span>` +
      `<span class="dn">${row.count}</span>` +
    "</div>").join("");
  const note = noisyNote(r);
  const wishes = r.wishes.length
    ? '<div class="wishes">' + r.wishes.map((w) =>
        `<div class="wish"><b>${esc(w.name)}</b> <span class="quiet">${w.stars}★ · ` +
        `${esc(w.day)}</span><p>${esc(w.text)}</p></div>`).join("") + "</div>"
    : '<p class="footnote">Пожеланий пока не писали.</p>';
  return (
    '<div class="card">' +
      '<p class="h">Оценки</p>' +
      `<p class="hsub">${esc(averageLabel(r))} · ${esc(ratingsWord(r.count))}</p>` +
      rows +
      (note ? `<p class="footnote warn">${esc(note)}</p>` : "") +
      wishes +
    "</div>"
  );
}

function ticketHtml(t: NonNullable<typeof state.admin>["tickets"][number]): string {
  const open = openTicket === t.id;
  const ctx = Object.entries(t.context || {});
  const buttons = STATUS_ORDER.map((s) =>
    `<button type="button" class="stbtn${t.status === s ? " on" : ""}" ` +
    `data-tstatus="${s}" data-tid="${t.id}"${busy ? " disabled" : ""}>` +
    `${esc(statusLabel(s))}</button>`).join("");
  return (
    `<div class="tkadm${open ? " open" : ""}">` +
      `<div class="tkline" data-topen="${t.id}">` +
        `<span class="tkid">#${t.id}</span>` +
        `<span class="tkwho">${esc(t.name ?? "—")}</span>` +
        `<span class="tktxt">${esc(t.text)}</span>` +
        `<span class="tkst ${esc(t.status)}">${esc(t.status_label)}</span>` +
      "</div>" +
      (open
        ? '<div class="tkbody">' +
            (ctx.length
              ? '<ul class="ctxlist">' + ctx.map(([k, v]) =>
                  `<li><b>${esc(k)}</b>: ${esc(v)}</li>`).join("") + "</ul>"
              : '<p class="footnote">Контекста нет — заявка пришла без него.</p>') +
            (t.answer ? `<p class="tkanswer">Ответ: ${esc(t.answer)}</p>` : "") +
            `<input class="ansin" id="ans-${t.id}" placeholder="Что ответить человеку (по желанию)" ` +
            `maxlength="500" value="${esc(t.answer ?? "")}">` +
            `<div class="stbtns">${buttons}</div>` +
            `<p class="footnote">${t.notified_status === t.status
              ? "О текущем статусе человеку сообщили."
              : "Про «чиним» и «готово» бот напишет сам; про «принято» — нет."}</p>` +
          "</div>"
        : "") +
    "</div>"
  );
}

export function renderAdmin(host: HTMLElement): void {
  const d = state.admin;
  if (!d) {
    host.innerHTML = failed
      ? '<div class="stub"><p>Сводка не открылась. Если это не твой аккаунт — ' +
        "так и должно быть: доступ закрывает сервер.</p></div>"
      : '<div class="stub"><p>Собираем сводку…</p></div>';
    if (!loading && !failed) void load();
    return;
  }

  const p = d.people;
  const a = d.activity;
  const s = d.server;

  host.innerHTML =
    '<div class="two-col admin">' +
      '<div class="col-main">' +
        '<div class="greet"><h2>Админ</h2>' +
        `<span class="streak">🛠 ${d.tickets_open}</span></div>` +
        '<div class="sumstrip">' +
          `<div class="sumcell"><div class="sv">${num(p.users)}</div>` +
            '<div class="sl">человек завелось</div></div>' +
          `<div class="sumcell"><div class="sv">${num(p.new_7d)}</div>` +
            '<div class="sl">за неделю</div></div>' +
          `<div class="sumcell"><div class="sv">${num(d.study.studying)}</div>` +
            '<div class="sl">занимались за 7 дней</div></div>' +
          `<div class="sumcell"><div class="sv">${num(a.answers_24h)}</div>` +
            '<div class="sl">ответов за сутки</div></div>' +
        "</div>" +
        '<div class="card">' +
          `<p class="h">Заявки · ${esc(ticketsWord(d.tickets.length))}` +
          `${d.tickets_open ? ` · открытых ${d.tickets_open}` : ""}</p>` +
          (d.tickets.length
            ? d.tickets.map(ticketHtml).join("")
            : '<p class="hsub">Пока никто не жаловался. Это либо хорошо, либо ' +
              "никто ещё не дошёл до поломки.</p>") +
        "</div>" +
        ratingsHtml() +
      "</div>" +
      '<div class="col-side">' +
        '<div class="card">' +
          '<p class="h">Сервер</p>' +
          `<div class="set-row"><div class="lbl"><div class="t">База</div>` +
            `<div class="s">${esc(s.db)} · таблиц ${s.tables}</div></div>` +
            `<div class="val">${kb(s.db_bytes)}</div></div>` +
          '<div class="set-row"><div class="lbl"><div class="t">Последний бэкап</div>' +
            `<div class="s">${s.backup ? esc(s.backup.at) + " · всего " + s.backup.count
                                       : "не видно — проверь таймер"}</div></div>` +
            `<div class="val">${s.backup ? kb(s.backup.bytes) : "—"}</div></div>` +
          '<div class="set-row"><div class="lbl"><div class="t">Ответов всего</div>' +
            `<div class="s">за неделю ${num(a.answers_7d)}</div></div>` +
            `<div class="val">${num(a.answers_total)}</div></div>` +
          (a.push_failed_7d
            ? '<div class="set-row"><div class="lbl"><div class="t">Напоминания не ушли</div>' +
              '<div class="s">за последнюю неделю</div></div>' +
              `<div class="val warn">${a.push_failed_7d}</div></div>`
            : "") +
        "</div>" +
        '<div class="card">' +
          '<p class="h">Доступ</p>' +
          `<p class="hsub">Открыт ${num(p.allowed)}, из них подарком ${num(p.gifts)}. ` +
          `Закрыт ${num(p.denied)}.${d.channel.on ? "" : " ⚠ Ворота выключены."}</p>` +
          '<div class="giftrow">' +
            '<input class="ansin" id="giftId" inputmode="numeric" ' +
              'placeholder="telegram_id" aria-label="telegram_id">' +
            '<button type="button" class="askbtn yes" id="giftGo">Подарить</button>' +
          "</div>" +
          '<p class="footnote">Подарок открывает вход мимо проверки подписки — ' +
          "им же владелец не запирает сам себя.</p>" +
        "</div>" +
        '<button type="button" class="askbtn no" data-go="settings">← В настройки</button>' +
      "</div>" +
    "</div>";

  host.querySelectorAll<HTMLElement>("[data-topen]").forEach((el) => {
    el.addEventListener("click", () => {
      tap(el);
      const id = Number(el.dataset.topen);
      openTicket = openTicket === id ? null : id;
      render("admin");
    });
  });

  host.querySelectorAll<HTMLElement>("button[data-tstatus]").forEach((btn) => {
    btn.addEventListener("click", () => {
      if (busy) return;
      tap(btn);
      const id = Number(btn.dataset.tid);
      const answer = host.querySelector<HTMLInputElement>(`#ans-${id}`)?.value.trim();
      busy = true;
      render("admin");
      void adminTicket(id, btn.dataset.tstatus as string, answer || undefined)
        .catch(() => undefined)
        .then(() => { busy = false; return load(); });
    });
  });

  host.querySelector("#giftGo")?.addEventListener("click", () => {
    const field = host.querySelector<HTMLInputElement>("#giftId");
    const id = Number((field?.value ?? "").trim());
    if (!id) { field?.focus(); return; }
    tap(field);
    void adminGift(id).catch(() => undefined).then(() => load());
  });
}
